import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: 'https://www.behance.net/ceilkey', label: 'Work', external: true },
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/life-beyond-work', label: 'Life Beyond Work' },
  { to: '/contact', label: 'Contact' },
];

const NavLink = ({ to, label, onClick, isActive, external }) => {
  const className = `
    relative py-2 text-sm tracking-wide transition-colors duration-200
    ${isActive
      ? 'text-warm-gray-900'
      : 'text-warm-gray-500 hover:text-warm-gray-800'
    }
  `;

  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className={className}
      >
        {label}
      </a>
    );
  }

  return (
    <Link
      to={to}
      onClick={onClick}
      className={className}
    >
      {label}
      {isActive && (
        <span className="absolute -bottom-1 left-0 w-full h-px bg-warm-gray-900" />
      )}
    </Link>
  );
};

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const closeMenu = () => setIsOpen(false);

  return (
    <nav className="py-6 md:py-8">
      <div className="flex justify-between items-center">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center hover:opacity-80 transition-opacity"
        >
          <img
            src="/images/logo.png"
            alt="Deepali Agrawal"
            className="h-8 md:h-10 w-auto"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-6">
          {navLinks.slice(1).map((link, index) => (
            <React.Fragment key={link.to}>
              {index > 0 && (
                <img
                  src="/images/flower-icon.svg"
                  alt=""
                  className="w-5 h-5 opacity-100"
                  aria-hidden="true"
                />
              )}
              <NavLink
                to={link.to}
                label={link.label}
                isActive={!link.external && location.pathname === link.to}
                external={link.external}
              />
            </React.Fragment>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden p-2 -mr-2 text-warm-gray-700 hover:text-warm-gray-900 transition-colors"
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`
          fixed lg:hidden inset-0 bg-cream z-50 transition-all duration-300 ease-in-out
          ${isOpen
            ? 'opacity-100 translate-x-0'
            : 'opacity-0 translate-x-full pointer-events-none'
          }
        `}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="py-6 px-6 flex justify-between items-center border-b border-warm-gray-200">
            <Link
              to="/"
              className="flex items-center"
              onClick={closeMenu}
            >
              <img
                src="/images/logo.png"
                alt="Deepali Agrawal"
                className="h-8 w-auto"
              />
            </Link>
            <button
              onClick={closeMenu}
              className="p-2 -mr-2 text-warm-gray-700 hover:text-warm-gray-900 transition-colors"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          {/* Mobile Menu Links */}
          <div className="flex flex-col gap-1 p-6">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.to}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMenu}
                  className="py-3 text-lg transition-colors duration-200 text-warm-gray-600 hover:text-warm-gray-900"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={closeMenu}
                  className={`
                    py-3 text-lg transition-colors duration-200
                    ${location.pathname === link.to
                      ? 'text-warm-gray-900 font-medium'
                      : 'text-warm-gray-600 hover:text-warm-gray-900'
                    }
                  `}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* Mobile Menu Footer */}
          <div className="mt-auto p-6 border-t border-warm-gray-200">
            <p className="text-sm text-warm-gray-500">
              © {new Date().getFullYear()} Deepali Agrawal
            </p>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;